/**
 * Query keys for the plan domain (`../core/keys.ts` is the convention).
 *
 * The locale is IN every key that reads localized names. `planItemView`'s
 * exercise names, an alternative's name and an exclusion's name are resolved
 * in the request's locale (`planQuery`'s docblock tells the mixed-language
 * story), so an English and a Portuguese read of the same plan are two cache
 * entries, not one entry that flips language. An omitted locale is written as
 * `"en"`, the server's own default, so it never makes a second key for the
 * same answer.
 */
export const DEFAULT_PLAN_LOCALE = "en";
export const planKeys = {
    /** Every plan read — what `publish` invalidates. */
    all: ["plan"],
    /** The plan in effect in one locale; `activeAll` is every locale. */
    active: (locale = DEFAULT_PLAN_LOCALE) => ["plan", "active", locale],
    activeAll: ["plan", "active"],
    draft: (locale = DEFAULT_PLAN_LOCALE) => ["plan", "draft", locale],
    draftAll: ["plan", "draft"],
    /**
     * `GET /me/plan/draft/diff` in one locale (T32). UNDER `draftAll` on
     * purpose: every write that changes the draft — an item write, a reject, a
     * generate — already invalidates `["plan", "draft"]`, and the diff is a
     * reading of the draft, so it goes stale with it. `"diff"` is never a
     * locale, so it never collides with `draft(locale)`.
     */
    draftDiff: (locale = DEFAULT_PLAN_LOCALE) => ["plan", "draft", "diff", locale],
    alternatives: (itemId, locale = DEFAULT_PLAN_LOCALE) => ["plan", "alternatives", itemId, locale],
    alternativesAll: ["plan", "alternatives"],
    candidates: (itemId, locale = DEFAULT_PLAN_LOCALE, limit) => ["plan", "candidates", itemId, locale, limit ?? null],
    /**
     * The one page of `GET /me/plan/items/:id/candidates` the app reads, as a
     * plain query (the picker's and the workout swap's). One element longer
     * than {@link planKeys.candidates}, so the two never share a cache entry,
     * while both stay under `candidatesAll` for the refusal list's writes.
     */
    candidatesPage: (itemId, locale = DEFAULT_PLAN_LOCALE, limit) => ["plan", "candidates", itemId, locale, limit ?? null, "page"],
    candidatesAll: ["plan", "candidates"],
    /** `GET /me/plan/sessions/:id/candidates` — what may be ADDED to a session
     *  for one muscle. */
    sessionCandidates: (sessionId, muscleId, locale = DEFAULT_PLAN_LOCALE, limit) => [
        "plan",
        "sessionCandidates",
        sessionId,
        muscleId,
        locale,
        limit ?? null,
    ],
    sessionCandidatesAll: ["plan", "sessionCandidates"],
    exclusions: (locale = DEFAULT_PLAN_LOCALE) => ["plan", "exclusions", locale],
    exclusionsAll: ["plan", "exclusions"],
};
/**
 * `GET /me/changes` is the ACCOUNT's history — plan, profile, measurements and
 * sessions merged — so it is its own domain rather than a plan sub-key: a
 * profile write (T20) invalidates `["changes"]` without also refetching the
 * plan, and a plan write invalidates both.
 */
export const changeKeys = {
    all: ["changes"],
    /** Keyed by locale too since T35: a plan entry's `items` carry exercise
     *  names in the request's language. */
    list: (limit, locale = DEFAULT_PLAN_LOCALE) => ["changes", "list", limit ?? null, locale],
};
/**
 * The prefix of `GET /me/pending` (T38, `data/progress`), whose `draft` signal
 * a draft write changes. Written here because T32's writes need it before
 * T38's folder exists; T38's key factory starts its keys with this, so the
 * invalidation below reaches whatever it builds under it.
 */
export const pendingKeys = {
    all: ["pending"],
};
//# sourceMappingURL=keys.js.map